import { validateClientDiagnostic, diagnosticRoutes, type ClientDiagnostic } from "../domain/clientDiagnostics";

declare const __LISTWORK_RELEASE__: string;
let context: { workspaceID: string; apiBaseURL: string } | null = null;
let recent: ClientDiagnostic[] = [];
let sent = 0;
let windowStarted = Date.now();

export function configureDiagnostics(workspaceID?: string, apiBaseURL?: string) {
  const next = workspaceID && apiBaseURL ? { workspaceID, apiBaseURL: apiBaseURL.replace(/\/$/, "") } : null;
  if (context?.workspaceID !== next?.workspaceID) recent = [];
  context = next;
}

export function latestDiagnostic() { return [...recent].reverse().find((event) => event.outcome === "failure") ?? recent[recent.length - 1]; }

function recordClientDiagnostic(operation: ClientDiagnostic["operation"], outcome: ClientDiagnostic["outcome"], durationMs = 0, failure?: ClientDiagnostic["failure"]): string {
  const path = typeof location === "undefined" ? "other" : location.pathname.split("/")[1] || "dashboard";
  const route = diagnosticRoutes.includes(path as ClientDiagnostic["route"]) ? path as ClientDiagnostic["route"] : "other";
  const event = validateClientDiagnostic({ id: crypto.randomUUID(), operation, outcome, route, durationMs: Math.min(86400000, Math.max(0, Math.round(durationMs))), release: typeof __LISTWORK_RELEASE__ === "undefined" ? "development" : __LISTWORK_RELEASE__, ...(failure ? { failure } : {}) });
  recent = [...recent.slice(-9), event];
  if (Date.now() - windowStarted > 60000) { sent = 0; windowStarted = Date.now(); }
  if (context && sent < 20) {
    sent++;
    const current = context;
    // Diagnostics never block a seller's action and never report their own failures.
    void fetch(`${current.apiBaseURL}/api/workspaces/${encodeURIComponent(current.workspaceID)}/client-events`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(event), signal: AbortSignal.timeout(5000), keepalive: true
    }).catch(() => undefined);
  }
  return event.id;
}

export function recordDiagnostic(operation: ClientDiagnostic["operation"], outcome: ClientDiagnostic["outcome"], durationMs = 0, failure?: ClientDiagnostic["failure"]): string {
  try { return recordClientDiagnostic(operation, outcome, durationMs, failure); }
  catch { return "unavailable"; }
}

export async function measureTask<T>(operation: ClientDiagnostic["operation"], work: () => Promise<T>): Promise<T> {
  const started = performance.now();
  try {
    const result = await work();
    recordDiagnostic(operation, "success", performance.now() - started);
    return result;
  } catch (error) {
    recordDiagnostic(operation, "failure", performance.now() - started, "operation");
    throw error;
  }
}

export function installErrorDiagnostics() {
  window.addEventListener("error", () => { recordDiagnostic("app", "failure", 0, "unhandled"); });
  window.addEventListener("unhandledrejection", () => { recordDiagnostic("app", "failure", 0, "unhandled"); });
}

export function supportEmail(reference: string, details = "") {
  return `mailto:support@listwork.co?subject=${encodeURIComponent("Listwork problem report")}&body=${encodeURIComponent(`${details}\n\nReference: ${reference}`)}`;
}
